import React, { useState, useEffect } from 'react';
import { X, Download, File, Image, Video, FileText } from 'lucide-react';
import { fetchWithTenant } from '../utils/apiClient';
import configs from '../config';

export function FileViewerModal({ isOpen, onClose, proposalId, attachmentIndex, attachment }) {
    const [fileData, setFileData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (isOpen && proposalId && attachmentIndex !== undefined) {
            loadFileData();
        }
    }, [isOpen, proposalId, attachmentIndex]);

    const loadFileData = async () => {
        try {
            setLoading(true);
            setError('');

            const response = await fetchWithTenant(`${configs.BACKEND_URL}/api/files/${proposalId}/${attachmentIndex}`, {
                method: 'GET',
            });

            if (!response.ok) {
                throw new Error('Failed to load file');
            }

            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            setFileData({ url, blob });
        } catch (error) {
            console.error('Error loading file:', error);
            setError('Failed to load file');
        } finally {
            setLoading(false);
        }
    };

    const handleDownload = () => {
        if (fileData && attachment) {
            const link = document.createElement('a');
            link.href = fileData.url;
            link.download = attachment.name;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    const getFileIcon = (mimeType) => {
        if (mimeType.startsWith('image/')) {
            return <Image className="h-8 w-8 text-blue-500" />;
        } else if (mimeType.startsWith('video/')) {
            return <Video className="h-8 w-8 text-purple-500" />;
        } else if (mimeType.includes('pdf')) {
            return <FileText className="h-8 w-8 text-red-500" />;
        } else {
            return <File className="h-8 w-8 text-gray-500" />;
        }
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const renderFileContent = () => {
        if (!fileData || !attachment) return null;

        const mimeType = attachment.mimeType || attachment.type;

        if (mimeType.startsWith('image/')) {
            return (
                <div className="flex justify-center">
                    <img
                        src={fileData.url}
                        alt={attachment.name}
                        className="max-w-full max-h-96 object-contain rounded-lg"
                    />
                </div>
            );
        } else if (mimeType.startsWith('video/')) {
            return (
                <div className="flex justify-center">
                    <video
                        src={fileData.url}
                        controls
                        className="max-w-full max-h-96 rounded-lg"
                    >
                        Your browser does not support the video tag.
                    </video>
                </div>
            );
        } else if (mimeType.includes('pdf')) {
            return (
                <div className="w-full h-96">
                    <iframe
                        src={fileData.url}
                        className="w-full h-full rounded-lg border"
                        title={attachment.name}
                    />
                </div>
            );
        } else {
            return (
                <div className="flex flex-col items-center justify-center h-64 bg-gray-50 rounded-lg">
                    {getFileIcon(mimeType)}
                    <p className="mt-4 text-gray-600">Preview not available for this file type</p>
                    <button
                        onClick={handleDownload}
                        className="mt-4 flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
                    >
                        <Download className="h-4 w-4" />
                        Download to view
                    </button>
                </div>
            );
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b">
                    <div className="flex items-center gap-3">
                        {attachment && getFileIcon(attachment.mimeType || attachment.type)}
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900">
                                {attachment?.name || 'File Viewer'}
                            </h3>
                            {attachment && (
                                <p className="text-sm text-gray-500">
                                    {attachment.mimeType || attachment.type} • {formatFileSize(attachment.size)}
                                </p>
                            )}
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {fileData && (
                            <button
                                onClick={handleDownload}
                                className="flex items-center gap-2 bg-gray-600 text-white px-3 py-2 rounded-md hover:bg-gray-700 transition-colors"
                            >
                                <Download className="h-4 w-4" />
                                Download
                            </button>
                        )}
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600"
                        >
                            <X className="h-6 w-6" />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
                    {loading && (
                        <div className="flex items-center justify-center h-64">
                            <div className="text-gray-500">Loading file...</div>
                        </div>
                    )}

                    {error && (
                        <div className="flex flex-col items-center justify-center h-64">
                            <div className="text-red-500 mb-4">{error}</div>
                            <button
                                onClick={loadFileData}
                                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
                            >
                                Retry
                            </button>
                        </div>
                    )}

                    {fileData && !loading && !error && renderFileContent()}
                </div>
            </div>
        </div>
    );
}
